import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';

import { Offer } from '@models/offer/offer.model';

import { OfferPriceIncreaseEntities } from '@models/offer/offerPriceIncrease.model';
import { PartialUserContactInfo } from '@models/user/user.model';

@Injectable()
export class OfferService {
  baseHref = `${environment.apiUrl}/offers`;

  constructor(private http: HttpClient) {}

  applyForTask(data: Offer): Observable<Offer> {
    const url = `${this.baseHref}`;
    return this.http.post<Offer>(url, data);
  }

  applyForIncreasedPrice(priceIncData: {
    message: string;
    censored: Date | null;
    priceBid: number;
    taskId: number;
  }): Promise<Offer> {
    const url = `${this.baseHref}/price-increase`;
    return firstValueFrom(this.http.post<any>(url, priceIncData));
  }

  cancelOffer(taskId: number): Observable<boolean> {
    const url = `${this.baseHref}/task/${taskId}/cancel`;
    return this.http.get<boolean>(url);
  }

  createOfferFromOfferRequest(data: Offer): Observable<Offer> {
    const url = `${this.baseHref}/offer-request`;
    return this.http.post<Offer>(url, data);
  }

  editOffer(data: any): Observable<Offer> {
    const url = `${this.baseHref}/edit/${data.offerId}`;
    return this.http.post<Offer>(url, data);
  }

  getOwnOffers(): Observable<Offer[]> {
    const url = `${this.baseHref}/own`;
    return this.http.get<Offer[]>(url);
  }

  getAcceptedOfferByTaskId(taskId: number): Observable<Offer> {
    const url = `${this.baseHref}/get-accepted-offer-by-task-id/${taskId}`;
    return this.http.get<Offer>(url);
  }

  editPriceIncreaseStatus(taskId: number, status: string): Promise<boolean> {
    const url = `${this.baseHref}/offer-increase-edit-status/${taskId}/${status}`;
    return firstValueFrom(this.http.get<boolean>(url));
  }

  calculatePlatformFee(
    priceBid: number,
    offerId?: number,
  ): Observable<{
    fee: number;
    amount: number;
    beforeCampaignFee: number;
    beforeCampaignAmount: number;
    campaign: any;
  }> {
    const url = `${this.baseHref}/fee`;
    return this.http.post<{
      fee: number;
      amount: number;
      beforeCampaignFee: number;
      beforeCampaignAmount: number;
      campaign: any;
    }>(url, { priceBid, offerId });
  }

  calculateRehirePlatformFee(
    priceBid: number,
  ): Observable<{ fee: number; amount: number }> {
    const url = `${this.baseHref}/rehire-flat-fee`;
    return this.http.post<{ fee: number; amount: number }>(url, { priceBid });
  }

  calculatePlatformFeePriceIncrease(
    priceBid: number,
    acceptedOfferId: number,
  ): Observable<{
    fee: number;
    amount: number;
    prevOffer: Offer;
    beforeCampaignFee: number;
    beforeCampaignAmount: number;
    campaign: any;
  }> {
    const url = `${this.baseHref}/price-increase-fee`;
    return this.http.post<{
      fee: number;
      amount: number;
      prevOffer: Offer;
      beforeCampaignFee: number;
      beforeCampaignAmount: number;
      campaign: any;
    }>(url, { priceBid, acceptedOfferId });
  }

  getTaskByAcceptedOffer(offerId: number): Observable<any> {
    const url = `${this.baseHref}/accepted-offer/${offerId}`;
    return this.http.get<any>(url);
  }

  fetchOfferPriceIncreaseEntities(
    taskId: number,
  ): Promise<OfferPriceIncreaseEntities> {
    const url = `${this.baseHref}/price-inc-offer/${taskId}`;
    return firstValueFrom(this.http.get<OfferPriceIncreaseEntities>(url));
  }

  getOtherPartiesContactInfo(
    offerId: number,
  ): Observable<PartialUserContactInfo> {
    const url = `${this.baseHref}/${offerId}/contact-info`;
    return this.http.get<PartialUserContactInfo>(url);
  }

  getOffer(offerId: number): Observable<any> {
    const url = `${this.baseHref}/${offerId}`;
    return this.http.get<any>(url);
  }
}
